import { useCallback, useRef, useState } from 'react';

/**
 * 与 localStorage 同步的 state。
 *
 * 写入必须在 setStoredValue 调用时同步落盘,不能放进 setState 的更新函数里 ——
 * React 对更新函数的执行时机不做保证,若紧接着 navigate() 跳转,
 * 新页面挂载时可能还读不到刚写的值(Phase 1 项目已踩过这个坑)。
 */
export function useLocalStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored ? JSON.parse(stored) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const latestRef = useRef(value);

  const setStoredValue = useCallback(
    (next) => {
      const resolved = typeof next === 'function' ? next(latestRef.current) : next;
      latestRef.current = resolved;
      try {
        window.localStorage.setItem(key, JSON.stringify(resolved));
      } catch {
        // localStorage 不可用时静默忽略
      }
      setValue(resolved);
    },
    [key]
  );

  const clear = useCallback(() => {
    window.localStorage.removeItem(key);
    latestRef.current = initialValue;
    setValue(initialValue);
    // eslint-disable-next-line
  }, [key]);

  return [value, setStoredValue, clear];
}
